/**
 * MemorialManagePage — dedicated memorial workspace (not a person tab).
 * Route: /memorials/manage/:personRef
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { ThemeToggle } from '../components/ThemeToggle';
import { MemorialTab } from '../components/memorial/MemorialTab';
import { MemorialKeepsakesPanel } from '../components/memorial/MemorialKeepsakesPanel';
import { ArchiveEmptyState } from '../components/archive/ArchiveEmptyState';
import { getApiBase } from '../utils/apiBase';
import { personDisplayName, memorialsHref } from '../utils/memorial';
import { personHref } from '../utils/prettyArchiveLinks';
import { humanizeError } from '../utils/errorHandling';
import { ContextualGuideTip } from '../components/guide/ContextualGuideTip';
import { HelpContextLink } from '../components/help';
import {
  MEMORIAL_BRIDGE_PILLAR_LABEL,
  MEMORIAL_BRIDGE_WORKSPACE_TAGLINE,
} from '../content/memorialBridgeContent';
import { FAMILY_ARCHIVE_MEMORIAL_CREDIT_WORKSPACE_NOTE } from '../content/familyArchiveMemorialCredit';

const API = getApiBase();

export default function MemorialManagePage() {
  const { personRef } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [tree, setTree] = useState(null);
  const [storyCount, setStoryCount] = useState(0);
  const [documentCount, setDocumentCount] = useState(0);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishingKeepsake, setPublishingKeepsake] = useState(false);

  const load = useCallback(async () => {
    if (!personRef) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const personRes = await axios.get(`${API}/individuals/detail/${personRef}`);
      const loaded = personRes.data;
      setPerson(loaded);

      const [treeRes, storiesRes, docsRes, memRes] = await Promise.all([
        axios.get(`${API}/trees/${loaded.tree_id}`),
        axios.get(`${API}/individuals/${loaded.id}/stories`).catch(() => ({ data: [] })),
        axios.get(`${API}/documents/${loaded.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/memorials-api/by-person/${loaded.id}`),
      ]);
      setTree(treeRes.data);
      setStoryCount(Array.isArray(storiesRes.data) ? storiesRes.data.length : 0);
      setDocumentCount(Array.isArray(docsRes.data) ? docsRes.data.length : 0);
      setWorkspace(memRes.data);
    } catch (error) {
      toast.error(humanizeError(error));
      navigate('/memorials');
    } finally {
      setLoading(false);
    }
  }, [personRef, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const canEdit = tree?.role === 'owner' || tree?.role === 'editor';
  const name = personDisplayName(person);

  const handlePublishMemorial = useCallback(async () => {
    const memorialId = workspace?.memorial?.id;
    if (!memorialId) return;
    setPublishingKeepsake(true);
    try {
      await axios.post(`${API}/memorials-api/${memorialId}/publish`, {});
      toast.success('Memorial page published.', {
        description: 'Your memorial link is now available. Content still respects your visibility settings.',
      });
      await load();
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setPublishingKeepsake(false);
    }
  }, [workspace?.memorial?.id, load]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-label="Loading memorial workspace" />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="min-h-screen bg-background" data-testid="memorial-manage-empty">
        <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6">
          <ArchiveEmptyState
            title="Choose a person to prepare a memorial page"
            description="Memorial pages are prepared from a person profile. Open a loved one's profile, then choose Prepare Memorial."
            actions={
              <>
                <Button type="button" className="rounded-full" asChild>
                  <Link to="/dashboard">Open your archive</Link>
                </Button>
                <Button type="button" variant="outline" className="rounded-full" asChild>
                  <Link to="/memorials">All memorials</Link>
                </Button>
              </>
            }
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="memorial-manage-page">
      <header className="sticky top-0 z-30 border-b border-border/40 bg-[hsl(42_28%_99%)]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button type="button" variant="ghost" size="sm" className="rounded-full shrink-0" asChild>
              <Link to={person ? personHref(person) : '/dashboard'}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Profile
              </Link>
            </Button>
            <div className="min-w-0 hidden sm:block">
              <p className="truncate font-serif text-sm text-foreground">{name}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{MEMORIAL_BRIDGE_PILLAR_LABEL}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HelpContextLink section="memorial-bridge" className="hidden sm:inline">
              Memorial guide
            </HelpContextLink>
            <Button type="button" variant="outline" size="sm" className="rounded-full hidden sm:inline-flex" asChild>
              <Link to={memorialsHref(person?.tree_id)}>All memorials</Link>
            </Button>
            <Link to="/dashboard" className="shrink-0 hidden md:block">
              <BrandWordmark className="h-6" />
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <ContextualGuideTip tipId="memorial-workspace" title="Memorial Bridge" className="mb-6">
          {MEMORIAL_BRIDGE_WORKSPACE_TAGLINE} Private until you publish. Tributes are moderated by default.
        </ContextualGuideTip>
        <p className="mb-6 text-xs leading-relaxed text-muted-foreground/90">
          {FAMILY_ARCHIVE_MEMORIAL_CREDIT_WORKSPACE_NOTE}
        </p>
        <MemorialTab
          person={person}
          canEdit={canEdit}
          storyCount={storyCount}
          documentCount={documentCount}
          onMemorialUpdated={(resp) => {
            if (resp?.memorial) {
              setPerson((prev) => ({
                ...(prev || {}),
                memorial_summary: resp.memorial,
              }));
              setWorkspace((prev) => ({ ...(prev || {}), ...resp }));
            } else {
              load();
            }
          }}
          onRefreshPerson={load}
        />

        <div className="mt-8">
          <MemorialKeepsakesPanel
            memorial={workspace?.memorial}
            publicUrl={workspace?.public_url}
            personName={name}
            person={person}
            onPublishMemorial={canEdit ? handlePublishMemorial : undefined}
            publishing={publishingKeepsake}
          />
        </div>
      </main>
    </div>
  );
}
